/**
 * Tracks the Labor CTA paid variants, metafield gifts, and discount code.
 * If those paid variants leave the cart, the matching gifts and code are removed.
 *
 * Claim lives in localStorage so every tab can see it. Mutations are hooked on
 * both window.fetch and window.__originalFetch so skip-cart / other PDPs that
 * add via the native fetch alias still wait for the strip before checkout.
 */
(function () {
  'use strict';

  if (window.laborCtaDiscount) return;

  var STORAGE_KEY = 'larineLaborCtaDiscount';
  var DEFAULT_PROP = '_labor_gift';
  var ENAMEL_PROP = '_enamel_gift';
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var nativeFetch = (window.__originalFetch || window.fetch).bind(window);

  var syncChain = Promise.resolve();

  function cartRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.slice(-1) === '/' ? r : r + '/';
  }

  function cartJs(path) {
    return cartRoot() + path;
  }

  function storageGet() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) raw = sessionStorage.getItem(STORAGE_KEY);
      return raw;
    } catch (e) {
      return null;
    }
  }

  function parseIdList(list) {
    return (Array.isArray(list) ? list : [list])
      .map(function (id) { return parseInt(id, 10); })
      .filter(function (id) { return id > 0; });
  }

  function mergeIds(a, b) {
    var merged = [];
    var seen = {};
    (a || []).concat(b || []).forEach(function (id) {
      if (seen[id]) return;
      seen[id] = true;
      merged.push(id);
    });
    return merged;
  }

  var OWNER_PROP = '_free_gift_for';

  function claimBaseId(id) {
    var raw = String(id || 'labor');
    var cut = raw.indexOf(':');
    return cut === -1 ? raw : raw.slice(0, cut);
  }

  function parseProductId(value) {
    var n = parseInt(value, 10);
    return n > 0 ? String(n) : '';
  }

  function normalizeClaim(id, data) {
    var variantIds = parseIdList(data && data.variantIds);
    if (!variantIds.length) return null;
    var base = claimBaseId(id);
    var productId = parseProductId(data && data.productId);
    if (!productId && id && String(id).indexOf(':') !== -1) {
      productId = parseProductId(String(id).slice(String(id).indexOf(':') + 1));
    }
    var prop = String((data && (data.prop || data.giftProp)) || (base === 'enamel' ? ENAMEL_PROP : DEFAULT_PROP));
    return {
      id: id,
      code: String((data && data.code) || '').trim(),
      variantIds: variantIds,
      giftIds: parseIdList(data && data.giftIds),
      prop: prop,
      productId: productId,
    };
  }

  function readStore() {
    try {
      var raw = storageGet();
      if (!raw) return { claims: {} };
      var data = JSON.parse(raw);
      var claims = {};
      if (data && data.claims && typeof data.claims === 'object' && !Array.isArray(data.claims)) {
        Object.keys(data.claims).forEach(function (id) {
          var claim = normalizeClaim(id, data.claims[id]);
          if (claim) claims[id] = claim;
        });
      } else {
        var legacy = normalizeClaim('labor', data);
        if (legacy) claims.labor = legacy;
      }
      return { claims: claims };
    } catch (e) {
      return { claims: {} };
    }
  }

  function claimList() {
    var claims = readStore().claims;
    return Object.keys(claims).map(function (id) { return claims[id]; });
  }

  function hasClaims() {
    return claimList().length > 0;
  }

  function writeStore(claims) {
    var out = {};
    Object.keys(claims || {}).forEach(function (id) {
      var claim = claims[id];
      if (!claim || !parseIdList(claim.variantIds).length) return;
      out[id] = {
        code: String(claim.code || '').trim(),
        variantIds: parseIdList(claim.variantIds),
        giftIds: parseIdList(claim.giftIds),
        prop: String(claim.prop || DEFAULT_PROP),
        productId: parseProductId(claim.productId),
      };
    });
    if (!Object.keys(out).length) {
      clearClaim();
      return;
    }
    writeClaim({ claims: out });
  }

  function writeClaim(claim) {
    var raw = JSON.stringify(claim);
    try {
      localStorage.setItem(STORAGE_KEY, raw);
    } catch (e) {}
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function clearClaim() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function remember(codeOrOpts, variantIds, giftIds) {
    var opts = codeOrOpts && typeof codeOrOpts === 'object' && !Array.isArray(codeOrOpts)
      ? codeOrOpts
      : { code: codeOrOpts, variantIds: variantIds, giftIds: giftIds };
    var baseId = claimBaseId(opts.id || 'labor');
    var productId = parseProductId(opts.productId);
    var id = productId ? baseId + ':' + productId : baseId;
    var nextIds = parseIdList(opts.variantIds);
    if (!nextIds.length) return;
    var store = readStore();
    var prev = store.claims[id] || {
      code: '',
      variantIds: [],
      giftIds: [],
      prop: baseId === 'enamel' ? ENAMEL_PROP : DEFAULT_PROP,
      productId: productId,
    };
    if (!productId) productId = parseProductId(prev.productId);
    var nextCode = Object.prototype.hasOwnProperty.call(opts, 'code')
      ? String(opts.code || '').trim()
      : prev.code;
    store.claims[id] = {
      id: id,
      code: nextCode,
      variantIds: mergeIds(prev.variantIds, nextIds),
      giftIds: mergeIds(prev.giftIds, parseIdList(opts.giftIds)),
      prop: String(opts.giftProp || opts.prop || prev.prop || DEFAULT_PROP),
      productId: productId,
    };
    writeStore(store.claims);
  }

  function discountCodesFromCart(cart) {
    var out = [];
    var seen = {};
    function push(raw) {
      var code = String(raw || '').trim();
      if (!code) return;
      var key = code.toUpperCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push(code);
    }
    if (cart && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(function (entry) {
        push(typeof entry === 'string' ? entry : entry && entry.code);
      });
    }
    if (cart && typeof cart.discount_code === 'string') push(cart.discount_code);
    return out;
  }

  function cartHasCode(cart, code) {
    var upper = String(code || '').trim().toUpperCase();
    if (!upper) return false;
    return discountCodesFromCart(cart).some(function (c) {
      return c.toUpperCase() === upper;
    });
  }

  function cartHasLaborProduct(cart, variantIds) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var wanted = {};
    variantIds.forEach(function (id) { wanted[id] = true; });
    return items.some(function (item) {
      var id = parseInt(item.variant_id || item.id, 10);
      return wanted[id] && parseInt(item.quantity, 10) > 0;
    });
  }

  function isFullCart(payload) {
    return !!(payload && Array.isArray(payload.items) && 'item_count' in payload);
  }

  function fetchCart() {
    return nativeFetch(cartJs('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    });
  }

  function applySections(sections) {
    if (sections && window.cartDrawerCustom && typeof window.cartDrawerCustom.applySections === 'function') {
      window.cartDrawerCustom.applySections(sections);
    }
  }

  function itemProperties(item) {
    return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function giftPropValue(props, prop) {
    if (!props || !prop) return '';
    return String(props[prop] || '');
  }

  function isProtectedGiftProp(props) {
    return giftPropValue(props, DEFAULT_PROP) === '1' || giftPropValue(props, ENAMEL_PROP) === '1';
  }

  function itemOwnerId(item) {
    return parseProductId(itemProperties(item)[OWNER_PROP]);
  }

  function isClaimGiftLine(item, claim) {
    var props = itemProperties(item);
    var prop = claim.prop || DEFAULT_PROP;
    var owner = itemOwnerId(item);
    if (claim.productId && owner && owner !== claim.productId) return false;
    if (giftPropValue(props, prop) === '1') return true;
    if (claim.productId && owner === claim.productId) {
      return parseInt(item.final_price, 10) === 0 || parseInt(item.price, 10) === 0;
    }
    if (isProtectedGiftProp(props)) return false;
    var id = parseInt(item.variant_id || item.id, 10);
    if (!id || (claim.giftIds || []).indexOf(id) === -1) return false;
    return parseInt(item.final_price, 10) === 0 || parseInt(item.price, 10) === 0;
  }

  function claimGiftUpdates(cart, claim) {
    var updates = {};
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function (item) {
      if (!isClaimGiftLine(item, claim)) return;
      var key = item.key || String(item.variant_id || item.id);
      if (key) updates[key] = 0;
    });
    return updates;
  }

  function cartHasClaimGift(cart, giftId, claim) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var prop = claim.prop || DEFAULT_PROP;
    return items.some(function (item) {
      var id = parseInt(item.variant_id || item.id, 10);
      if (id !== giftId || parseInt(item.quantity, 10) <= 0) return false;
      if (giftPropValue(itemProperties(item), prop) !== '1') return false;
      var owner = itemOwnerId(item);
      if (claim.productId && owner && owner !== claim.productId) return false;
      return true;
    });
  }

  function giftLineProperties(claim) {
    var properties = {};
    properties[claim.prop || DEFAULT_PROP] = '1';
    if (claim.productId) properties[OWNER_PROP] = claim.productId;
    return properties;
  }

  function ensureClaimGifts(cart, claim) {
    var giftIds = claim.giftIds || [];
    var adds = [];
    giftIds.forEach(function (id) {
      if (cartHasClaimGift(cart, id, claim)) return;
      adds.push({ id: id, quantity: 1, properties: giftLineProperties(claim) });
    });
    if (!adds.length) return Promise.resolve(null);
    return nativeFetch(cartJs('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: adds, sections: 'cart-drawer,cart-icon-bubble' }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/add.js ' + res.status);
      return res.json();
    });
  }

  function applyClaimCleanup(cart, claim) {
    var payload = { sections: 'cart-drawer,cart-icon-bubble' };
    var updates = claimGiftUpdates(cart, claim);
    var hasUpdates = Object.keys(updates).length > 0;
    var hasCode = !!(claim.code && cartHasCode(cart, claim.code));
    if (hasUpdates) payload.updates = updates;
    if (hasCode) {
      payload.discount = discountCodesFromCart(cart)
        .filter(function (c) { return c.toUpperCase() !== claim.code.toUpperCase(); })
        .join(',');
    }
    if (!hasUpdates && !hasCode) return Promise.resolve(null);
    return nativeFetch(cartJs('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js ' + res.status);
      return res.json();
    });
  }

  function processClaims(cart, claims, index, lastUpdated) {
    if (index >= claims.length) {
      var remaining = {};
      claims.forEach(function (claim) {
        if (claim._keep) remaining[claim.id] = claim;
      });
      writeStore(remaining);
      return Promise.resolve(lastUpdated);
    }
    var claim = claims[index];
    var action = cartHasLaborProduct(cart, claim.variantIds)
      ? (claim._keep = true, ensureClaimGifts(cart, claim))
      : (claim._keep = false, applyClaimCleanup(cart, claim));
    return action.then(function (updated) {
      var nextCart = isFullCart(updated) ? updated : cart;
      return processClaims(nextCart, claims, index + 1, updated || lastUpdated);
    });
  }

  function runSync(cartHint) {
    var claims = claimList();
    if (!claims.length) return Promise.resolve({ changed: false });

    window.__laborCtaDiscountUpdating = true;
    var cartPromise = isFullCart(cartHint) ? Promise.resolve(cartHint) : fetchCart();
    return cartPromise
      .then(function (cart) {
        return processClaims(cart, claims, 0, null);
      })
      .then(function (updated) {
        if (updated && updated.sections) applySections(updated.sections);
        return {
          changed: !!updated,
          sections: updated && updated.sections,
        };
      })
      .catch(function () {
        return { changed: false };
      })
      .then(function (result) {
        window.__laborCtaDiscountUpdating = false;
        return result || { changed: false };
      });
  }

  function sync(cartHint) {
    if (!hasClaims()) return Promise.resolve({ changed: false });
    syncChain = syncChain.then(function () {
      return runSync(cartHint);
    }).catch(function () {
      return { changed: false };
    });
    return syncChain;
  }

  function hrefOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function wrapFetch(fn) {
    return function (input) {
      var url = hrefOf(input);
      var req = fn.apply(this, arguments);
      if (!CART_MUTATION_RE.test(url) || window.__laborCtaDiscountUpdating) {
        return req;
      }
      return req.then(function (res) {
        if (!res.ok) return res;
        var hintPromise = res.clone().json().then(function (payload) {
          return isFullCart(payload) ? payload : null;
        }).catch(function () {
          return null;
        });
        return hintPromise.then(function (hint) {
          return sync(hint);
        }).then(function () {
          return res;
        });
      });
    };
  }

  window.__originalFetch = wrapFetch(nativeFetch);
  if (window.fetch) {
    window.fetch = wrapFetch(window.fetch.bind(window));
  }

  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__laborCtaCartUrl = url;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (CART_MUTATION_RE.test(String(xhr.__laborCtaCartUrl || ''))) {
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300) sync(null);
      });
    }
    return origSend.apply(this, arguments);
  };

  function boot() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw && !localStorage.getItem(STORAGE_KEY)) {
        localStorage.setItem(STORAGE_KEY, raw);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {}
    sync(null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('pageshow', function () {
    sync(null);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') sync(null);
  });

  window.addEventListener('focus', function () {
    sync(null);
  });

  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) sync(null);
  });

  document.addEventListener('cart:refresh', function () {
    sync(null);
  });

  window.laborCtaDiscount = {
    remember: remember,
    sync: sync,
    clear: clearClaim,
  };
})();
